import React, { useState, useEffect, useRef, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import CloseNotificationIcon from 'components/common/icons/redesign/CloseNotificationIcon';

import NotificationsEmitter from 'emitters/NotificationsEmitter';
import { NOTIFICATIONS_FORMATTER } from 'helpers/notificationsHelpers';
import { getAvatar } from 'components/Notifications/NotificationItem/NotificationItem';

import { Controls } from 'components/Interview/OnlineInterview/RightPanel/WaitingRoomList/WaitingRoomList';
import style from './ToastNotifications.module.scss';
import { BrowserContext } from 'context/OnlineInterview/BrowserContext';
import { ChatContext } from 'context/ChatContext';
import { ROOT_SIZE } from 'styles/variables';

const ToastNotifications = () => {
    const [notifications, setNotifications] = useState([]);
    const { browser } = useContext(BrowserContext);
    const { openedDialog } = useContext(ChatContext);

    useEffect(() => {
        if (!browser.isMobile) {
            NotificationsEmitter.on('notification::new', addNotification);
            NotificationsEmitter.on('popup::new', addNotification);
        }
        return () => {
            NotificationsEmitter.off('notification::new', addNotification);
            NotificationsEmitter.off('popup::new', addNotification);
        };
    }, [openedDialog]);

    const addNotification = (n) => {
        setNotifications((notifications) => [...notifications, { ...n, offset: 0 }]);
    };

    const closeNotification = (n, offset) => {
        setNotifications((notifications) =>
            notifications.filter((notification, index) => {
                if (index < notifications.indexOf(n)) {
                    notification.offset = notification.offset - offset - 10;
                }
                return notification !== n;
            }),
        );
    };

    const setOffset = (n, offset) => {
        setNotifications((notifications) =>
            notifications.map((notification) => {
                if (n !== notification) {
                    notification.offset = notification.offset + offset + 10;
                }
                return notification;
            }),
        );
    };

    return (
        <div className={style.toastNotificationsContainer}>
            {notifications.map((notification) => {
                if (notification.type === 'waitingRoomReady') {
                    return (
                        <InterviewReadyNotification
                            notification={notification}
                            closeNotification={closeNotification}
                            setOffset={setOffset}
                            key={notification.source.id}
                        />
                    );
                }

                return (
                    <NotificationItem
                        notification={notification}
                        closeNotification={closeNotification}
                        setOffset={setOffset}
                        key={notification._id}
                    />
                );
            })}
        </div>
    );
};

const InterviewReadyNotification = withRouter(({ notification, closeNotification, setOffset }) => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [unmounted, setUnmounted] = useState(true);
    const [closing, setClosing] = useState(false);

    const close = () => {
        closeNotification(notification, containerRef.current.clientHeight);
    };

    useEffect(() => {
        setTimeout(() => setUnmounted(false), 0);
        setOffset(notification, containerRef.current.clientHeight);

        let innerTimeout;
        const timeoutId = setTimeout(() => {
            setClosing(true);
            innerTimeout = setTimeout(close, 400);
        }, 10000);
        return () => {
            clearTimeout(timeoutId);
            clearTimeout(innerTimeout);
        };
    }, []);
    const avatarURL = getAvatar(notification);
    return (
        <div
            className={cn(style.interviewNotification, {
                [style.unmounted]: unmounted,
                [style.closing]: closing,
            })}
            ref={containerRef}
            style={{ bottom: `${notification.offset / ROOT_SIZE}rem` }}
        >
            <div className={style.topSection}>
                <div className={style.upcoming}>{t('waitingRoom:upcoming')}:</div>
                <CloseNotificationIcon
                    className={cn(style.closeIcon, style.interviewClose)}
                    onClick={close}
                    height='30'
                    width='30'
                />
            </div>
            <div className={style.readyNotificationContentWrapper}>
                <div className={style.avatar} style={{ backgroundImage: `url(${avatarURL})` }} />
                <div className={cn(style.interviewReadyNotificationContent, style.content)}>
                    <div className={style.infoContainer}>
                        <div className={style.name}>{notification.source.displayName}</div>
                        <div className={style.status}>{t('waitingRoom:readyNotification')}</div>
                    </div>
                    <Controls user={notification.source} buttonClassName={style.button} />
                </div>
            </div>
        </div>
    );
});

const NotificationItem = withRouter(({ notification, closeNotification, history, setOffset, offset }) => {
    const containerRef = useRef(null);
    const [unmounted, setUnmounted] = useState(true);
    const [closing, setClosing] = useState(false);
    const [closed, setClosed] = useState(false);
    const close = () => {
        setClosed(true);
        closeNotification(notification, containerRef.current.clientHeight);
    };

    useEffect(() => {
        setTimeout(() => setUnmounted(false), 0);
        setOffset(notification, containerRef.current.clientHeight);
        if (notification.type === 'constantReload') {
            return;
        }

        let innerTimeout;
        const timeoutId = setTimeout(() => {
            setClosing(true);
            innerTimeout = setTimeout(close, 400);
        }, 10000);
        return () => {
            clearTimeout(timeoutId);
            clearTimeout(innerTimeout);
        };
    }, []);
    const { t } = useTranslation();

    const avatarURL = notification.avatar || getAvatar(notification);
    const content = NOTIFICATIONS_FORMATTER[notification.type](notification.type, notification.data, history, t, close);
    return (
        <div
            className={cn(style.toastNotification, {
                [style.unmounted]: unmounted,
                [style.closing]: closing,
                [style.closed]: closed,
            })}
            ref={containerRef}
            style={{ bottom: `${notification.offset}px` }}
        >
            <div className={style.avatar} style={{ backgroundImage: `url(${avatarURL})` }} />
            <div className={style.content}>{content}</div>
            <CloseNotificationIcon className={style.closeIcon} onClick={close} />
        </div>
    );
});

export default ToastNotifications;
